import { normalToFace } from '../voxelLiterals'
import { isInBounds, isInBoundsXYZ, parseKey, parseKeyX, parseKeyXYZ, parseKeyY, parseKeyZ, posKey, posKeyXYZ } from '../voxelMath'
import * as VoxelRendering from '../voxelRendering'
import { clientPosFromEvent, raycastLayers, type MirrorRaycastMode } from '../voxelRendering'
import { e as makeEvent, type EventTuple } from '@/events/types'
import type { Hit, VoxelMap, VoxelData } from '../voxelTypes'
import type { Surface } from '@/core/surface'
import type { ScribbleSettings } from '@/uiState'
import { type Spine, type SpineEntry } from './spineCore'
import { canonicalVoxelType, usesSurfaceOrientation, preferSeedFace, placementFaceAllowed, placementPolicy, providesPlacementSurface, voxelHitPlacement } from '@/voxels/registry'
import { computeTopFaceOnSurface, computeVoxelFace } from '@/helpers/orientation'
import { axisFromAnchorToPoint } from "../core/axis"
import { cellCenter } from '@/core/grid'
import { voxelLineAlongAxis } from "../core/iter"
import { projectPointerToLineScalar } from '@/core/project'
import { screenAxisAlignmentChoice, screenAxisDistanceChoice } from '@/overlays/screenSpaceHelpers'
import { rasterizeSphere, rasterizeCube, rasterizeDisc } from '@/shapes'
import { writeFn } from '@/events/layerSystem'
import { tintStrokeNeverOverwrites } from '@/voxels/tint/tintConstants'
import { Occupancy2D } from '@/core/surface'
import type { DrawingSurfaces } from '@/interactions/drawingSurfaces'

// Toggle: choose how straight-scribble determines its endpoint
// - 'world': uses world hit point rounding (existing behavior)
// - 'screen': uses screen-position projection onto the axis line from anchor
export const STRAIGHT_SCRIBBLE_END_MODE: 'world' | 'screen' = 'screen'
// Axis choice heuristic when STRAIGHT_SCRIBBLE_END_MODE === 'screen'
export const STRAIGHT_SCRIBBLE_AXIS_CHOICE: 'alignment' | 'distance' = 'distance'

const ERASER_VOXEL = { color: 0xff0000, type: 'solid' }

// Spine types are now defined in spineCore.ts and imported for consistency

const spineLastKey = (spine: Spine | null | undefined): number | undefined => {
  if (!spine || spine.length === 0) return undefined
  return spine[spine.length - 1]?.key
}

const findSpineEntry = (spine: Spine | null | undefined, key: number): SpineEntry | undefined => {
  if (!spine) return undefined
  const found = spine.find((point) => point.key === key)
  return found
    ? { voxel: found.voxel, normal: found.normal, hitType: found.hitType, surfacePlacement: found.surfacePlacement, mirror: found.mirror }
    : undefined
}


// Convert spine to simple voxel map for raycasting
export function spineToVoxelMap(spine: Spine): VoxelMap {
  const voxelMap = new Map<number, any>()
  for (const point of spine) {
    voxelMap.set(point.key, point.voxel)
  }
  return voxelMap
}

function normalComponents(normal: { x: number; y: number; z: number }) {
  return {
    x: Math.round(normal.x) | 0,
    y: Math.round(normal.y) | 0,
    z: Math.round(normal.z) | 0,
  }
}

export function canPlaceVoxelOnSurface(
  type: string,
  key: number,
  normal: { x: number; y: number; z: number },
  bounds: { x: number; y: number; z: number },
  target: VoxelMap,
  opts?: { allowOccupiedTarget?: boolean },
): boolean {
  const policy = placementPolicy(type)
  if (policy.target !== 'surface') return true

  const face = normalToFace(normal)
  if (!placementFaceAllowed(type, face)) return false

  const x = parseKeyX(key)
  const y = parseKeyY(key)
  const z = parseKeyZ(key)
  if (!isInBoundsXYZ(x, y, z, bounds)) return false
  const targetVoxel = target.get(key)
  if (targetVoxel && (!opts?.allowOccupiedTarget || !providesPlacementSurface(targetVoxel.type))) return false

  const n = normalComponents(normal)
  const backing = { x: x - n.x, y: y - n.y, z: z - n.z }
  if (isInBoundsXYZ(backing.x, backing.y, backing.z, bounds)) {
    if (policy.requireBackingSurface === false) return true
    const backingVoxel = target.get(posKeyXYZ(backing.x, backing.y, backing.z))
    return !!backingVoxel && providesPlacementSurface(backingVoxel.type)
  }

  return !!policy.allowDocumentPlanes
}

export function canStartSurfacePlacement(
  type: string,
  hit: Hit,
  baseLayer: VoxelMap,
  bounds: { x: number; y: number; z: number },
): boolean {
  const policy = placementPolicy(type)
  if (policy.target !== 'surface') return true

  const face = normalToFace(hit.normal)
  if (!placementFaceAllowed(type, face)) return false

  if (hit.type === 'plane') {
    return !!policy.allowDocumentPlanes && isInBoundsXYZ(hit.position.x, hit.position.y, hit.position.z, bounds)
  }

  const hitVoxel = hit.voxel
  if (!providesPlacementSurface(hitVoxel?.type)) return false

  const n = normalComponents(hit.normal)
  const adjacent = { x: hit.position.x + n.x, y: hit.position.y + n.y, z: hit.position.z + n.z }
  if (!isInBoundsXYZ(adjacent.x, adjacent.y, adjacent.z, bounds)) {
    return canPlaceVoxelOnSurface(type, posKeyXYZ(hit.position.x, hit.position.y, hit.position.z), hit.normal, bounds, baseLayer, {
      allowOccupiedTarget: true,
    })
  }

  return canPlaceVoxelOnSurface(type, posKeyXYZ(adjacent.x, adjacent.y, adjacent.z), hit.normal, bounds, baseLayer)
}

export function placeSurfaceSpineCenters(
  spine: Spine,
  bounds: { x: number; y: number; z: number },
  base: VoxelMap,
  subtract?: boolean,
): Spine {
  const placed: Spine = []
  for (const entry of spine) {
    const type = entry.voxel.type
    const key = entry.key
    const cx = parseKeyX(key)
    const cy = parseKeyY(key)
    const cz = parseKeyZ(key)
    const normal = entry.normal || { x: 0, y: 0, z: 0 }

    if (!subtract && entry.hitType === 'voxel') {
      const n = normalComponents(normal)
      const adj = { x: cx + n.x, y: cy + n.y, z: cz + n.z }
      if (!isInBoundsXYZ(adj.x, adj.y, adj.z, bounds)) {
        if (!canPlaceVoxelOnSurface(type, key, normal, bounds, base, { allowOccupiedTarget: true })) continue
        placed.push({ key, voxel: entry.voxel, normal, hitType: 'plane', surfacePlacement: 'replaceBoundary' })
        continue
      }
      const adjKey = posKeyXYZ(adj.x, adj.y, adj.z)
      if (!canPlaceVoxelOnSurface(type, adjKey, normal, bounds, base)) continue
      placed.push({ key: adjKey, voxel: entry.voxel, normal, hitType: 'plane' })
      continue
    }

    const replaceBoundary = entry.surfacePlacement === 'replaceBoundary'
    if (canPlaceVoxelOnSurface(type, key, normal, bounds, base, { allowOccupiedTarget: replaceBoundary })) {
      placed.push({ key, voxel: entry.voxel, normal, hitType: 'plane', ...(replaceBoundary && { surfacePlacement: 'replaceBoundary' as const }) })
    }
  }
  return placed
}

// Expand a spine and compute per-voxel faces for oriented types using the stored seed normals.
// Aligns scribble preview with final commit behavior.
export type ScribbleOpts = {
  shape?: 'sphere' | 'cube' | 'disc'
  placement?: 'center' | 'onTop'
  /** Scoped dig (B5): a subtract stroke removes only cells of this canonical type. */
  onlyType?: string
}

const canonicalVoxelTypeOf = (type: string | undefined): string | undefined =>
  type === undefined ? undefined : canonicalVoxelType(type)

export function expandSpineWithNormals(
  spine: Spine,
  radius: number,
  bounds: { x: number; y: number; z: number },
  target: VoxelMap,
  subtract?: boolean,
  opts?: ScribbleOpts,
): void {
  const r = radius < 0.5 ? 0.5 : radius
  const useShape = opts?.shape || 'sphere'
  const scope = subtract && opts?.onlyType ? { onlyType: opts.onlyType } : null
  // Tint writes empty cells only (tintStrokeNeverOverwrites); every other type overwrites.
  const fillOnly = !subtract && tintStrokeNeverOverwrites(canonicalVoxelTypeOf(spine[0]?.voxel?.type))
  const writer = writeFn(subtract ? 'subtract' : fillOnly ? 'fill' : 'place', target, undefined, scope)

  let stepIndex = 0
  for (const point of spine) {
    const key = point.key
    const entry = point
    const cx = parseKeyX(key)
    const cy = parseKeyY(key)
    const cz = parseKeyZ(key)
    const voxelData = entry.voxel
    const seedNormal = entry.normal
    const topFace = voxelData.topFace
    const allowOccupiedTarget = entry.surfacePlacement === 'replaceBoundary'
    // Determine base placement position P: prefer adjacent along normal when possible (draw mode only)
    const nx = Math.round(seedNormal.x) | 0
    const ny = Math.round(seedNormal.y) | 0
    const nz = Math.round(seedNormal.z) | 0
    const adj = { x: cx + nx, y: cy + ny, z: cz + nz }
    const adjInBounds = isInBoundsXYZ(adj.x, adj.y, adj.z, bounds)
    const adjKey = posKeyXYZ(adj.x, adj.y, adj.z)
    const tryAdjacent = !subtract && ((entry as any)?.hitType === 'voxel')
    const placePos = (tryAdjacent && adjInBounds && !target.has(adjKey)) ? adj : { x: cx, y: cy, z: cz }

    // Apply placement option: 'center' => center at P; 'onTop' => put bounding edge at P
    const half = (useShape === 'disc') ? 0 : Math.floor(r)
    const cc = (opts?.placement === 'onTop')
      ? { x: placePos.x + nx * half, y: placePos.y + ny * half, z: placePos.z + nz * half }
      : placePos

    switch (useShape) {
      case 'cube': {
        rasterizeCube(cc, r, voxelData, bounds, (key) => {
          if (subtract) { writer(key, voxelData); return }
          if (!canPlaceVoxelOnSurface(voxelData.type, key, seedNormal, bounds, target, { allowOccupiedTarget })) return
          if (usesSurfaceOrientation(voxelData.type)) {
            const face = preferSeedFace(voxelData.type) ? normalToFace(seedNormal) : computeVoxelFace(target, ...parseKeyXYZ(key), bounds, seedNormal)
            writer(key, { ...voxelData, face, ...(topFace && { topFace }) })
          } else {
            if ((target as any).get(key) === voxelData) return
            writer(key, voxelData)
          }
        })
        break
      }
      case 'disc': {
        // Disc plane is perpendicular to the quantized seed normal
        const face = normalToFace(seedNormal)
        rasterizeDisc(cc, r, face || 'y+', voxelData, bounds, (k) => {
          if (subtract) { writer(k, voxelData); return }
          if (!canPlaceVoxelOnSurface(voxelData.type, k, seedNormal, bounds, target, { allowOccupiedTarget })) return
          if (usesSurfaceOrientation(voxelData.type)) {
            const f = preferSeedFace(voxelData.type) ? (face || 'y+') : computeVoxelFace(target, ...parseKeyXYZ(key), bounds, seedNormal)
            writer(k, { ...voxelData, face: f, ...(topFace && { topFace }) })
          } else {
            if ((target as any).get(k) === voxelData) return
            writer(k, voxelData)
          }
        })
        break
      }
      default: {
        // sphere (default)
        rasterizeSphere(cc, r, voxelData, bounds, (key) => {
          if (subtract) { writer(key, voxelData); return }
          if (!canPlaceVoxelOnSurface(voxelData.type, key, seedNormal, bounds, target, { allowOccupiedTarget })) return
          if (usesSurfaceOrientation(voxelData.type)) {
            const face = preferSeedFace(voxelData.type) ? normalToFace(seedNormal) : computeVoxelFace(target, ...parseKeyXYZ(key), bounds, seedNormal)
            writer(key, { ...voxelData, face, ...(topFace && { topFace }) })
          } else {
            writer(key, voxelData)
          }
        })
        break
      }
    }
    stepIndex++
  }
  return
}


// Thin a connected spine by sampling one out of every N centers (N>=1). Keeps first and then each Nth step.
export function applySpacingToSpine(spine: Spine, step: number): Spine {
  if (!spine || spine.length === 0 || step <= 1) return spine
  const out: Spine = []
  for (let i = 0; i < spine.length; i++) {
    if (i % step === 0) out.push(spine[i])
  }
  return out
}

export function scribbleEntry(
  event: PointerEvent,
  baseLayer: VoxelMap,
  existingSpine: Spine,
  camera: any,
  canvas: HTMLCanvasElement,
  bounds: any,
  toolState: { voxelData: VoxelData, scribble?: { radius?: number, placement?: 'center' | 'onTop', subcell?: boolean } },
  subtract: boolean,
  surface: Surface | Occupancy2D | null | undefined,
  mirrors: { x?: number; y?: number; z?: number } | undefined,
  drawingSurfaces: DrawingSurfaces | undefined,
  mirrorMode: MirrorRaycastMode,
  /** Extra layers an erase picks against besides `baseLayer` (re-digging, src/interactions/redig.ts). */
  erasePickLayers?: VoxelMap[],
): [number, SpineEntry] | null {
  
  const ray = VoxelRendering.screenToRay(clientPosFromEvent(event), camera, canvas)
  // TODO - see how in draft mode we can coalesce entries *at each step* so that we can somehow 
  // have access to the whole spine map.
  const target = subtract
    ? (erasePickLayers && erasePickLayers.length ? [...erasePickLayers, baseLayer] : [baseLayer])
    : [spineToVoxelMap(existingSpine), baseLayer]
  const hit = raycastLayers(ray, target, bounds, surface ?? undefined, mirrors, drawingSurfaces, {
    mirrorMode,
  })
  if (!hit) return null

  const blockType = toolState.voxelData.type
  if (!subtract && !canStartSurfacePlacement(blockType, hit, baseLayer, bounds)) return null

  const placeHitVoxel = !subtract &&
    hit.type === 'voxel' &&
    voxelHitPlacement(blockType) === 'hit' &&
    canonicalVoxelType(hit.voxel?.type ?? '') === canonicalVoxelType(blockType)
  const targetPos = placeHitVoxel || subtract ? hit.position : VoxelRendering.placementPosition(hit, baseLayer, bounds)
  const key = posKey(targetPos)

  if (key === spineLastKey(existingSpine) || !isInBounds(targetPos, bounds)) return null

  const face = usesSurfaceOrientation(blockType) ? normalToFace(hit.normal) : undefined
  const topFace = usesSurfaceOrientation(blockType) && face ? computeTopFaceOnSurface(face, camera) : undefined
  const n = normalComponents(hit.normal)
  const adjacent = { x: hit.position.x + n.x, y: hit.position.y + n.y, z: hit.position.z + n.z }
  const replaceBoundary = !subtract && hit.type === 'voxel' && !isInBoundsXYZ(adjacent.x, adjacent.y, adjacent.z, bounds)
  return [posKey(targetPos), {
    voxel: subtract ? ERASER_VOXEL : { ...toolState.voxelData, ...(face && { face }), ...(topFace && { topFace }) },
    normal: hit.normal,
    hitType: placeHitVoxel ? hit.type : 'plane',
    ...(hit.mirror && { mirror: true as const }),
    ...(replaceBoundary && { surfacePlacement: 'replaceBoundary' as const }),
    ...(toolState.scribble?.subcell && hit.exactPoint && { point: { x: hit.exactPoint.x, y: hit.exactPoint.y, z: hit.exactPoint.z } }),
  }]
}

// Build a shift-line from the first placed voxel to a cursor-projected end along the closest axis
export function axisConstrainedSpine(
  firstKey: number | undefined,
  hit: any,
  voxel: any,
  bounds: { x: number; y: number; z: number },
  lockedNormal?: { x: number; y: number; z: number },
  lockedHitType?: 'voxel' | 'plane',
  ev?: PointerEvent,
  camera?: any,
  rect?: any,
): Spine | null {
  if (firstKey == null) return null
  const anchor = parseKey(firstKey)
  const p = hit.exactPoint
    ? { x: hit.exactPoint.x, y: hit.exactPoint.y, z: hit.exactPoint.z }
    : cellCenter(hit.position)
  // Choose axis: in screen mode, choose by alignment or distance in screen space; otherwise use world-based heuristic
  let axis: 'x' | 'y' | 'z'
  if (STRAIGHT_SCRIBBLE_END_MODE === 'screen' && ev && camera && rect) {
    const anchorCenter = cellCenter(anchor)
    const px = ev.clientX - rect.left
    const py = ev.clientY - rect.top
    if (STRAIGHT_SCRIBBLE_AXIS_CHOICE === 'alignment') {
      axis = screenAxisAlignmentChoice(camera, rect, anchorCenter, px, py).axis
    } else {
      axis = screenAxisDistanceChoice(camera, rect, anchorCenter, px, py).axis
    }
  } else {
    axis = axisFromAnchorToPoint(anchor, p)
  }
  // Nearest grid index along varying axis with clamping
  const end = { x: anchor.x, y: anchor.y, z: anchor.z }
  if (STRAIGHT_SCRIBBLE_END_MODE === 'screen' && ev && camera && rect) {
    // Compute end purely from screen position relative to the axis through the anchor center
    const origin = cellCenter(anchor)
    const dir = axis === 'x' ? { x: 1, y: 0, z: 0 }
      : axis === 'y' ? { x: 0, y: 1, z: 0 }
        : { x: 0, y: 0, z: 1 }
    const s = projectPointerToLineScalar(ev, camera, rect, origin, dir)
    if (axis === 'x') {
      const ex = anchor.x + Math.round(s)
      end.x = Math.max(0, Math.min(bounds.x - 1, ex))
    } else if (axis === 'y') {
      const ey = anchor.y + Math.round(s)
      end.y = Math.max(0, Math.min(bounds.y - 1, ey))
    } else {
      const ez = anchor.z + Math.round(s)
      end.z = Math.max(0, Math.min(bounds.z - 1, ez))
    }
  } else {
    // World-based rounding of hit point (existing behavior)
    if (axis === 'x') {
      const ex = Math.floor(p.x + 0.5)
      end.x = Math.max(0, Math.min(bounds.x - 1, ex))
    } else if (axis === 'y') {
      const ey = Math.floor(p.y + 0.5)
      end.y = Math.max(0, Math.min(bounds.y - 1, ey))
    } else {
      const ez = Math.floor(p.z + 0.5)
      end.z = Math.max(0, Math.min(bounds.z - 1, ez))
    }
  }

  // Lock normal to the anchor's at creation time to avoid flicker/flip as cursor moves
  const normal = lockedNormal || hit.normal || { x: 0, y: 1, z: 0 }
  const spine: Spine = []
  const hType = lockedHitType || ((hit as any)?.type || 'voxel')
  for (const pos of voxelLineAlongAxis(anchor, end, axis)) {
    spine.push({ key: posKeyXYZ(pos.x, pos.y, pos.z), voxel, normal, hitType: hType })
  }
  return spine
}

export const computeLineEnd = (
  anchorKey: number,
  ui: any,
  base: VoxelMap,
  bounds: any,
  camera: any
) => {
  const hitInfo = ui.cursorHit
  let anchorNormal = hitInfo?.normal || { x: 0, y: 1, z: 0 }
  let anchorHitType: 'voxel' | 'plane' = 'voxel'
  if (ui.scribbleSpine) {
    const entry = findSpineEntry(ui.scribbleSpine as Spine | null, anchorKey)
    if (entry?.normal) anchorNormal = entry.normal
    if (entry?.hitType) anchorHitType = entry.hitType
  }

  if (!ui.shiftDown) return posKey(VoxelRendering.placementPosition(hitInfo, base, bounds))

  const spine = axisConstrainedSpine(anchorKey, hitInfo, {}, bounds, anchorNormal as any, anchorHitType as any, ui.clientPos, camera, ui.canvasRect)
  if (spine && spine.length > 0) {
    const last = spineLastKey(spine)
    if (last != null) return last
  }
  return null
}

/**
   * Coalesce two scribble events into one.
   * - Accepts both scribble.apply and scribble.append
   * - Only merges scribble.append events (from drafts)
   * - Never merges scribble.apply with scribble.apply (each is a separate stroke)
   * - Always returns scribble.apply (which IS an undo boundary)
   * - This converts draft events (append) into durable events (apply)
   */
export const combineScribbleEvents = function (prev: EventTuple, next: EventTuple): EventTuple | null {
  const pt = prev[0]
  const nt = next[0]
  const isScribblePrev = (pt === 'scribble.apply' || pt === 'scribble.append')
  const isScribbleNext = (nt === 'scribble.apply' || nt === 'scribble.append')
  if (!isScribblePrev || !isScribbleNext) return null

  // CRITICAL: Never merge two scribble.apply events - they're separate strokes!
  // Only merge scribble.append (draft events) into a single scribble.apply
  if (pt === 'scribble.apply' && nt === 'scribble.apply') return null

  type ScribblePayload = { spine: Spine; scribble: ScribbleSettings; subtract: boolean; onlyType?: string }
  const [, prevPayload] = prev as ['scribble.apply' | 'scribble.append', ScribblePayload]
  const [, nextPayload] = next as ['scribble.apply' | 'scribble.append', ScribblePayload]
  if (!!prevPayload.subtract !== !!nextPayload.subtract) return null
  if (prevPayload.onlyType !== nextPayload.onlyType) return null
  if (prevPayload.scribble !== nextPayload.scribble) return null
  const merged: Spine = [...prevPayload.spine, ...nextPayload.spine]
  // Always emit scribble.apply (undo boundary), even if inputs were scribble.append
  return makeEvent(['scribble.apply', {
    spine: merged,
    scribble: prevPayload.scribble,
    subtract: prevPayload.subtract,
    ...(prevPayload.onlyType ? { onlyType: prevPayload.onlyType } : {}),
  }] as const)
}
